import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { ProductionTranslationService } from './core/services/production-translation.service';
import { filter } from 'rxjs/operators';
import { HeaderFigmaCompleteComponent } from './components/header-figma-complete/header-figma-complete.component';
import { FooterComponent } from './footer/footer.component';
import { NotificationToastContainerComponent } from './shared/components/notification-toast-container/notification-toast-container.component';
import { NotificationConfirmationDialogComponent } from './shared/components/notification-confirmation-dialog/notification-confirmation-dialog.component';
import { NotificationLoadingOverlayComponent } from './shared/components/notification-loading-overlay/notification-loading-overlay.component';
import { StaleBundleBannerComponent } from './shared/components/stale-bundle-banner/stale-bundle-banner.component';
import { MaintenanceScreenComponent } from './shared/components/maintenance-screen/maintenance-screen.component';
import { ReferenceDataService } from './core/services/reference-data.service';
import { AuthService } from './core/services/auth.service';
import { isMaintenanceBypassProfile, isMaintenanceExemptUrl } from './core/models/runtime-flags';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.css',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        HeaderFigmaCompleteComponent,
        FooterComponent,
        NotificationToastContainerComponent,
        NotificationConfirmationDialogComponent,
        NotificationLoadingOverlayComponent,
        StaleBundleBannerComponent,
        MaintenanceScreenComponent,
    ]
})
export class AppComponent implements OnInit {
  title = 'BeFret';
  isBackoffice = false; // Also true for /portal — hides frontoffice header/footer

  // ── #1182 lot 2 — mode maintenance ──
  /** L'application est-elle remplacée par l'écran de maintenance, ici et maintenant ? */
  maintenanceActive = false;
  maintenanceMessage = '';
  maintenanceUntil: Date | null = null;

  private currentUrl = '/';
  private isStaff = false;

  constructor(
    private translationService: ProductionTranslationService,
    private router: Router,
    private refData: ReferenceDataService,
    private authService: AuthService,
  ) {}

  ngOnInit() {
    // Track if we're in backoffice to hide public header/footer
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    ).subscribe(e => {
      this.isBackoffice = e.urlAfterRedirects.startsWith('/backoffice') || e.urlAfterRedirects.startsWith('/portal');
      this.currentUrl = e.urlAfterRedirects;
      this.refreshMaintenance();
    });
    // Initial check
    this.isBackoffice = this.router.url.startsWith('/backoffice') || this.router.url.startsWith('/portal');
    this.currentUrl = this.router.url;

    // #1182 lot 2 — trois entrées, une seule décision.
    //
    // Le drapeau arrive en TEMPS RÉEL (`runtimeFlags`) et non par le cache de
    // 30 min : mettre le site en maintenance, et surtout l'en SORTIR, doit
    // prendre effet tout de suite. Le profil change à la connexion, l'URL à
    // chaque navigation — d'où trois abonnements qui convergent sur le même
    // recalcul plutôt qu'une valeur figée au démarrage.
    this.refData.runtimeFlags().subscribe(() => this.refreshMaintenance());
    // ⚠️ `error:` obligatoire. Une erreur non gérée TERMINE le flux : le profil
    // resterait figé sur sa dernière valeur, et un membre du staff connecté
    // pendant l'incident se retrouverait coupé sans moyen de rouvrir. Une
    // lecture de profil qui échoue ne doit pas décider de qui pilote.
    this.authService.getUserProfileObservable().subscribe({
      next: profile => {
        this.isStaff = isMaintenanceBypassProfile(profile);
        this.refreshMaintenance();
      },
      error: err => console.warn('[App] profil indisponible pour le bypass maintenance:', err),
    });
    this.refreshMaintenance();

    console.log('🚀 BeFret: Initializing production translation system...');
    this.translationService.translationsLoaded$.subscribe(loaded => {
      if (loaded) {
        console.log('✅ BeFret: Translation system ready for production');
      }
    });
  }

  /**
   * #1182 lot 2 — la maintenance coupe-t-elle CE visiteur, sur CETTE page ?
   *
   * Trois conditions, et les deux dernières ne sont pas des politesses :
   *
   * - le staff traverse, sinon on ne peut plus rien piloter pendant l'incident ;
   * - les routes d'authentification et les postes de pilotage traversent, sinon
   *   on s'enferme dehors — le drapeau qui rouvre le site se trouve derrière
   *   l'écran d'administration, lequel se trouve derrière la page de connexion.
   *
   * ⚠️ Fail-open : tout ce qui n'est pas `enabled === true` laisse le site
   * debout (`readMaintenanceFlag`).
   */
  private refreshMaintenance(): void {
    const flag = this.refData.getMaintenanceSync();
    this.maintenanceMessage = flag.message;
    this.maintenanceUntil = flag.until;
    this.maintenanceActive = flag.enabled
      && !this.isStaff
      && !isMaintenanceExemptUrl(this.currentUrl);
  }
}
